import React, {FunctionComponent, useEffect, useRef, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {Grid, Popover, Theme} from "@mui/material";
import moment from "moment/moment";
import {useSelector} from "react-redux";
import {selectModules} from "@/Redux/App/AppReducer";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

interface BookingPopoverProps {
    lineItem: any;
    popoverPositionX: number;
    popoverPositionY: number;
    open: boolean;
    handlePopoverClose: () => void;
}

const useStyles = makeStyles()((theme: Theme) => ({
    popover: {
        pointerEvents: "none",
    },

    paper: {
        padding: theme.spacing(1),
    },

    extrasIcon: {
        marginRight: 5,
        marginBottom: 8,
        display: "inline-flex",
        fontSize: 14,
    },
}));

const VehiclePopover: FunctionComponent<BookingPopoverProps> = ({
                                                                    lineItem,
                                                                    popoverPositionX,
                                                                    popoverPositionY,
                                                                    open,
                                                                    handlePopoverClose,
                                                                }): JSX.Element => {
    const {classes, cx} = useStyles();
    const modules = useSelector((state) => selectModules(state));
    const {t} = useTranslation();

    const [isDelaying, setIsDelaying] = useState(true);
    const [delayRunning, setDelayRunning] = useState(false);
    const [rerenderOnce, setRerenderOnce] = useState(false);

    if (rerenderOnce) {
        // Necessary for correct initial position
        setRerenderOnce(false);
    }

    const isMounted = useRef(false);

    const finishDelay = () => {
        if (isMounted.current) {
            setIsDelaying(false);
            setRerenderOnce(true);
        }
    };

    if (isDelaying && !delayRunning) {
        setDelayRunning(true);
        setTimeout(finishDelay, 500);
    }

    useEffect(() => {
        // Prevent memory leak
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    let myExtras = [];
    if (lineItem.equipment !== null) {
        myExtras = lineItem.equipment.split(",");
    }

    const ref = useRef(null);

    const bodyWidth =
        document?.getElementById("content-container")?.offsetWidth || 0;
    const bodyHeight =
        document?.getElementById("content-container")?.offsetHeight || 0;

    let horizontal = "left";
    let vertical = "top";
    // @ts-ignore
    if (popoverPositionX + ref?.current?.clientWidth >= bodyWidth) {
        horizontal = "right";
    }
    // @ts-ignore
    if (popoverPositionY + ref?.current?.offsetHeight >= bodyHeight) {
        vertical = "bottom";
    }

    let posY = popoverPositionY;
    if (vertical === "bottom") {
        posY -= 20;
    } else {
        posY += 20;
    }

    if (isDelaying) {
        return <></>;
    }

    return (
        <Popover
            anchorReference="anchorPosition"
            anchorPosition={{top: posY, left: popoverPositionX}}
            anchorOrigin={{
                // @ts-ignore
                vertical: vertical,
                // @ts-ignore
                horizontal: horizontal,
            }}
            transformOrigin={{
                // @ts-ignore
                vertical: vertical,
                // @ts-ignore
                horizontal: horizontal,
            }}
            className={classes.popover}
            classes={{
                paper: classes.paper,
            }}
            open={open}
            onClose={handlePopoverClose}
            disableRestoreFocus
        >
            <div ref={ref}>
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.vehicle.vehicleId")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem.vehicleId}</b>
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.vehicle.licensePlate")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem.licensePlate}</b>
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.vehicle.description")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem.name}</b>
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.vehicle.vehicleGroup")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem.vehicleGroup.vehicleGroupId}</b>
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.vehicle.location")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>
                            {lineItem.location.stationId} -{" "}
                            {lineItem.location.name}
                        </b>
                    </Grid>
                </Grid>
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.vehicle.mileage")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>
                            {lineItem.mileage} / {lineItem.maximumMileage}
                        </b>
                    </Grid>
                </Grid>
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.vehicle.equipment")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>
                            {myExtras.map((extra: string, index: number) => (
                                    <>
                                            <span
                                                key={"vehicleModal" + extra}
                                                className={cx(
                                                    classes.extrasIcon,
                                                    "cx9_fzga_ico cx9-" + extra
                                                )}
                                            />
                                        {extra}
                                        {index + 1 < myExtras.length ? <>{", "}</> : <></>}

                                    </>
                                )
                            )}
                        </b>
                    </Grid>
                </Grid>
                {modules.WIZARD && (
                    <Grid container spacing={0} style={{marginBottom: 10}}>
                        <Grid item xs={4}>
                            {t("popover.vehicle.wizardState")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>{lineItem.wizStatus}</b>
                        </Grid>
                        <Grid item xs={4}>
                            {t("popover.vehicle.wizardTurnback")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>
                                {moment
                                    .utc(lineItem.wizTurnbackDate)
                                    .format("DD.MM.YYYY HH:mm")}
                            </b>
                        </Grid>
                    </Grid>
                )}
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.vehicle.vehicleRemarks")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem.remarkVehicle}</b>
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.vehicle.remarks")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem.remark}</b>
                    </Grid>
                </Grid>
            </div>
        </Popover>
    );
};

export default VehiclePopover;
